import { type KeyboardEvent, useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useToast } from "@packsize/toast";

import { type MachineGroup } from "@/schemas";
import { scanImport } from "@/api/import-api";

interface UseScanToCreateInputParams {
	currentMachineGroup: MachineGroup | null | undefined;
}

export const useScanToCreateInput = ({ currentMachineGroup }: UseScanToCreateInputParams) => {
	const intl = useIntl();
	const inputRef = useRef<HTMLInputElement | null>(null);
	const { toastSuccess, toastError } = useToast();

	const [isLoading, setIsLoading] = useState(false);

	const handleKeyUp = useCallback(
		async (event: KeyboardEvent<HTMLInputElement>) => {
			if (!currentMachineGroup || inputRef.current === null) {
				return;
			}

			if (event.key === "Enter") {
				setIsLoading(true);

				try {
					await scanImport(currentMachineGroup.alias, `"${event.currentTarget.value}"`);
					toastSuccess({ message: intl.formatMessage({ id: "Scan Succeeded", defaultMessage: "Scan Succeeded" }) });
				} catch (e) {
					console.error("Error invoking scan import: ", e);
					toastError({ message: intl.formatMessage({ id: "Scan Failed", defaultMessage: "Scan Failed" }) });
				} finally {
					setIsLoading(false);
					inputRef.current.value = "";
					setTimeout(() => {
						if (inputRef.current === null) {
							return;
						}

						inputRef.current.focus();
					}, 200);
				}
			}
		},
		[currentMachineGroup, intl, toastError, toastSuccess],
	);

	return { onKeyUp: handleKeyUp, inputRef, isLoading };
};
